import { computed } from "vue";
import { jobsStore } from "@/store/jobs";
import { workerStore } from "@/store/worker";
import {
    buildWorkerOptions,
    findWorkerOption,
    WorkerOption,
    WorkerSelectOption
} from "@/utils/jobWorkerOptions";

import { i18n } from "@/i18n";

const t = (key: string, named?: Record<string, unknown>): string =>
	String(named ? i18n.global.t(key, named) : i18n.global.t(key));

/**
 * Текущий пользователь дополнительно закрепляется отдельной строкой наверху списка —
 * при этом он остаётся и на своём месте в списке (в своём отделе). Дубль живёт только
 * в разметке: закреплённые строки уходят в AppSelect отдельным пропом pinnedItems и не
 * попадают ни в items, ни в плоский список выбираемых элементов.
 */
export function useJobWorkerOptions() {
    const store = jobsStore();
    const userStore = workerStore();

    const workersOptions = computed<WorkerSelectOption[]>(() =>
        buildWorkerOptions(store.jobWorkers)
    );

    // Закрепляем только то, что реально нашли в списке работников, а не собираем строку
    // из workerProfile: закреплённая строка — это дубль строки из items, и её id обязан
    // быть тем же самым. Иначе галочка наверху живёт отдельно от списка — выбор отдела
    // её не отметит, а в WorkerIds уедет id, которого среди items нет.
    const pinnedCurrentUser = computed<WorkerOption[]>(() => {
        const found = findWorkerOption(store.jobWorkers, userStore.workerProfile?.Id);
        return found ? [found] : [];
    });

    // Одиночный фильтр: «Все» = не фильтровать. Пункт синтетический, в items его нет —
    // он закреплён сверху вместе с текущим пользователем, чтобы держать порядок
    // «Все → текущий пользователь → список». Пока работники не загрузились, закреплённых
    // строк тоже нет.
    const personFilterOptions = workersOptions;

    const personFilterPinned = computed<WorkerOption[]>(() => {
        if (!workersOptions.value.length) {
            return [];
        }
        return [
            {
                id: 0,
                label: t('all_label'),
                type: 'item'
            },
            ...pinnedCurrentUser.value
        ];
    });

    return {
        workersOptions,
        pinnedCurrentUser,
        personFilterOptions,
        personFilterPinned
    };
}
